import { useCallback, useEffect, useRef, useMemo, useState } from "react";

export type ProductCarouselControls = {
  currentIndex: number;
  goNext: () => void;
  goPrev: () => void;
  canGoNext: boolean;
  canGoPrev: boolean;
};

export type UseProductCarouselControlsOptions = {
  itemCount: number;
  listContainerRef: React.RefObject<HTMLUListElement | null>;
};

type CarouselState = {
  currentIndex: number;
  containerWidth: number;
  itemWidth: number;
};

export const useProductCarouselControls = ({
  itemCount,
  listContainerRef,
}: UseProductCarouselControlsOptions): ProductCarouselControls => {
  const [state, dispatch] = useState<CarouselState>({
    currentIndex: 0,
    containerWidth: 0,
    itemWidth: 0,
  });

  const scrollTimeoutRef = useRef<NodeJS.Timeout>();
  const isProgrammaticScrollingRef = useRef(false);
  const listContainer = listContainerRef.current;

  const maxIndex = useMemo(() => {
    const { containerWidth, itemWidth } = state;
    if (containerWidth === 0 || itemWidth === 0 || itemCount === 0) {
      return 0;
    }

    const lastItemEnd = itemCount * itemWidth;
    const maxScrollNeeded = lastItemEnd - containerWidth;

    if (maxScrollNeeded <= 0) {
      return 0;
    }

    return Math.ceil(maxScrollNeeded / itemWidth);
  }, [state, itemCount]);

  const clampedCurrentIndex = useMemo(() => {
    return Math.max(0, Math.min(state.currentIndex, maxIndex));
  }, [state.currentIndex, maxIndex]);

  useEffect(() => {
    if (!listContainer) return;

    let containerObserver: ResizeObserver | null = null;
    let itemObserver: ResizeObserver | null = null;

    containerObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        dispatch((prev) => ({ ...prev, containerWidth: width }));
      }
    });

    const measureAndObserveItem = () => {
      const firstItem = listContainer.querySelector("li") as HTMLElement;
      if (!firstItem) return;

      const itemWidth = firstItem.offsetWidth;
      if (itemWidth > 0) {
        dispatch((prev) => ({ ...prev, itemWidth }));
      }

      itemObserver = new ResizeObserver(() => {
        const updatedItemWidth = firstItem.offsetWidth;
        if (updatedItemWidth > 0) {
          dispatch((prev) => ({ ...prev, itemWidth: updatedItemWidth }));
        }
      });

      itemObserver.observe(firstItem);
    };

    containerObserver.observe(listContainer);

    const { width } = listContainer.getBoundingClientRect();
    dispatch((prev) => ({ ...prev, containerWidth: width }));
    measureAndObserveItem();

    return () => {
      containerObserver?.disconnect();
      itemObserver?.disconnect();
    };
  }, [listContainer, itemCount]);

  useEffect(() => {
    if (!listContainer || state.itemWidth === 0) return;

    const performProgrammaticScroll = () => {
      const targetScrollLeft = clampedCurrentIndex * state.itemWidth;
      isProgrammaticScrollingRef.current = true;

      listContainer.scrollTo({
        left: targetScrollLeft,
        behavior: "smooth",
      });

      setTimeout(() => {
        isProgrammaticScrollingRef.current = false;
      }, 50);
    };

    const handleUserScroll = () => {
      if (isProgrammaticScrollingRef.current) {
        return;
      }

      const scrollLeft = listContainer.scrollLeft;
      const maxScrollX = listContainer.scrollWidth - listContainer.clientWidth;

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        const calculatedIndex = Math.round(scrollLeft / state.itemWidth);
        const isAtEnd = scrollLeft >= maxScrollX - 5;
        const clampedIndex = isAtEnd
          ? maxIndex
          : Math.max(0, Math.min(calculatedIndex, maxIndex));

        dispatch((prev) => ({ ...prev, currentIndex: clampedIndex }));
      }, 50);
    };

    performProgrammaticScroll();
    listContainer.addEventListener("scroll", handleUserScroll, {
      passive: true,
    });

    return () => {
      isProgrammaticScrollingRef.current = false;
      listContainer.removeEventListener("scroll", handleUserScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [clampedCurrentIndex, state.itemWidth, maxIndex, listContainer]);

  const goNext = useCallback(() => {
    const nextIndex = Math.min(clampedCurrentIndex + 1, maxIndex);
    dispatch((prev) => ({ ...prev, currentIndex: nextIndex }));
  }, [clampedCurrentIndex, maxIndex]);

  const goPrev = useCallback(() => {
    const prevIndex = Math.max(clampedCurrentIndex - 1, 0);
    dispatch((prev) => ({ ...prev, currentIndex: prevIndex }));
  }, [clampedCurrentIndex]);

  const canGoPrev = clampedCurrentIndex > 0;
  const canGoNext = clampedCurrentIndex < maxIndex;

  return {
    currentIndex: clampedCurrentIndex,
    goNext,
    goPrev,
    canGoPrev,
    canGoNext,
  };
};
